import React, { useEffect, useState } from "react";
import { CaretUpOutlined, CaretDownOutlined, } from '@ant-design/icons';
import { Badge, Tooltip, } from "antd";
import { FormatEuro, } from ".";
import { subscribeToTicker, isTickerConnected } from "../../services/ticker-websocket";

const TickerCell = ({
    customer_id,
    exchange_id,
    symbol_id,
    position_data,
    ...props
}) => {
    const [bid, setBid] = useState(null);
    const [ask, setAsk] = useState(null);
    const [marketClosed, setMarketClosed] = useState(false);
    const [connected, setConnected] = useState(false);

    const { pnlValue, pnlPercent } = computePositionPnL(position_data, {
        bidRaw: bid,
        askRaw: ask,
    });

    useEffect(() => {
        const unsubscribe = subscribeToTicker(
            customer_id,
            exchange_id,
            symbol_id,
            (msg) => {
                if (typeof msg.bid === "number") setBid(msg.bid);
                if (typeof msg.ask === "number") setAsk(msg.ask);
                setMarketClosed(!!msg.market_closed);
                setConnected(isTickerConnected(customer_id, exchange_id, symbol_id));
            }
        );

        // Poll connection state until the first message arrives (best-effort indicator).
        const pollTimer = setInterval(() => {
            setConnected(isTickerConnected(customer_id, exchange_id, symbol_id));
        }, 1000);

        return () => {
            clearInterval(pollTimer);
            unsubscribe();
        };
    }, [customer_id, exchange_id, symbol_id]);

    return (
        <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>

            <span
                style={{
                    marginRight: 8,
                    // verticalAlign: "middle",
                }}>
                {connected ? (
                    <>
                        {marketClosed ?
                            <Tooltip title="Connected, but price is delayed"><Badge status="warning" /></Tooltip>
                            :
                            <Tooltip title="Connected, realtime"><Badge status="success" /></Tooltip>
                        }
                    </>
                ) : (
                    <Tooltip title="Wait for connection...">
                        <Badge status="processing" />
                    </Tooltip>
                )}
            </span>

            {/* <span
                title={connected ? "connected" : "disconnected"}

            /> */}
            <span className="d-none">{bid != null ? bid : "—"} / {ask != null ? ask : "—"}</span>
            <span className={`${pnlValue >= 0 ? 'gx-chart-up' : 'gx-chart-down'}`}>
                {pnlValue == null ? "—" : <FormatEuro amount={pnlValue.toFixed(2)}
                    currency={position_data.broker_price_data.exchange_currency} />}
                {" "}
                ({pnlPercent == null ? "—" : pnlPercent.toFixed(2)}%) {pnlPercent >= 0 ? (<CaretUpOutlined />) : (<CaretDownOutlined />)}
            </span>
        </span>
    );
};

export default TickerCell;



/**
 * Compute live PnL for a FX position using YOUR object fields.
 *
 * Assumptions (match your data):
 * - bid/ask coming from websocket/current_price_data are "scaled ints" (e.g. 94726 with digits=5 => 0.94726)
 * - entry price is available via:
 *    lotsize_calculation.entry  (preferred)
 *    OR position_result_data.deal[0].executionPrice
 * - pip value in account currency is available via lotsize_calculation.pip_value_per_lot_account
 * - lots is available via position_order_data.order_lotsize OR lotsize_calculation.lots
 * - pip size is derived from pip_position (default 4 for FX) => pipSize = 10^(-pip_position)
 *
 * Returns:
 *  {
 *    pnlValue: number|null,        // in account currency (e.g. EUR)
 *    pnlPercent: number|null,      // % relative to account balance (exchange_balance)
 *    priceUsed: number|null,       // close price used (bid for LONG, ask for SHORT)
 *    entry: number|null,
 *    pips: number|null
 *  }
 */
const lastLiveQuote = new Map();

export function computePositionPnL(position, { bidRaw, askRaw } = {}) {
    const dir = (position?.position_order_data?.order_direction || "LONG").toUpperCase();
    // const digits = Number(position?.broker_price_data?.current_price_data?.digits ?? position?.current_price_data?.digits ?? 5);

    // const normalizeSpot = (v) => (typeof v === "number" ? v / 100000 : null);
    const roundToDigits = (v) => (typeof v === "number" ? Number(v) : null);
    const isValidSide = (v) => typeof v === "number" && v > 0;

    const quoteKey = position?.journal_id;

    const liveBid = roundToDigits(typeof bidRaw === "number" ? bidRaw : null);
    const liveAsk = roundToDigits(typeof askRaw === "number" ? askRaw : null);

    const neededLive = dir === "SHORT" ? liveAsk : liveBid;

    if (isValidSide(liveBid) && isValidSide(liveAsk) && liveAsk >= liveBid) {
        lastLiveQuote.set(quoteKey, { bid: liveBid, ask: liveAsk, ts: Date.now() });
    } else if (isValidSide(neededLive)) {
        const prev = lastLiveQuote.get(quoteKey);
        lastLiveQuote.set(quoteKey, {
            bid: dir === "SHORT" ? (prev?.bid ?? null) : liveBid,
            ask: dir === "SHORT" ? liveAsk : (prev?.ask ?? null),
            ts: Date.now(),
        });
    }

    const cached = lastLiveQuote.get(quoteKey);
    const bid = cached?.bid ?? null;
    const ask = cached?.ask ?? null;

    const priceUsed = dir === "SHORT" ? ask : bid; // close LONG at bid, SHORT at ask
    if (!isValidSide(priceUsed)) {
        return { pnlValue: null, pnlPercent: null, priceUsed: null, entry: null, pips: null };
    }

    // ✅ ENTRY FIX: eerst echte entry/fill, daarna pas lotsize_calculation.entry
    const entry =
        Number(position?.position_order_data?.order_entry_price) ||
        Number(position?.position_result_data?.deal?.[0]?.executionPrice) ||
        Number(position?.broker_price_data?.lotsize_calculation?.entry) ||
        null;

    if (!isValidSide(entry)) {
        return { pnlValue: null, pnlPercent: null, priceUsed, entry: null, pips: null };
    }

    const lots = Number(
        position?.position_order_data?.order_lotsize ??
        position?.broker_price_data?.lotsize_calculation?.lots
    ) || null;

    const pipValuePerLot = Number(position?.broker_price_data?.lotsize_calculation?.pip_value_per_lot_account) || null;

    if (!isValidSide(lots) || !isValidSide(pipValuePerLot)) {
        return { pnlValue: null, pnlPercent: null, priceUsed, entry, pips: null };
    }

    // ✅ MAPPING FIX: pak exchange_pair_mapping op de juiste plek
    const pair = (position?.ts_pair || position?.broker_price_data?.lotsize_calculation?.symbol || "").toUpperCase();
    const mappingList =
        position?.broker_price_data?.exchange_pair_mapping ??
        position?.exchange_pair_mapping ??
        [];

    const mapping = mappingList.find((m) => (m?.code || "").toUpperCase() === pair);

    const pipPos = Number(mapping?.pair_data?.pip_position ?? 4);
    // const pipSize = Math.pow(10, -pipPos);
    const pipSize = pipPos;

    const diff = priceUsed - entry;
    let pips = diff / pipSize;
    if (dir === "SHORT") pips = -pips;

    // ✅ PnL (account currency) met jouw broker-waarde (werkt ook voor JPY als pips klopt)
    const pnlValue = pips * pipValuePerLot * lots;

    const balance =
        Number(position?.broker_price_data?.exchange_balance ??
            position?.broker_price_data?.lotsize_calculation?.balance) || null;
    const pnlPercent = balance ? (pnlValue / balance) * 100 : null;

    return { pnlValue, pnlPercent, priceUsed, entry, pips };
}
